import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineTeam } from 'react-icons/ai';
import { BsPersonPlus, BsTags } from 'react-icons/bs';
import { RiCheckboxCircleFill } from 'react-icons/ri';
import { TbZoom } from 'react-icons/tb';

import {
  Button,
  TextInput,
  ResponsiveModalBottomSheet,
  LoadingOverlay,
  Dropdown,
} from '@/components';
import ResponsiveModalPage from '@/components/ResponsiveModalPage';
import type { Treatment } from '@/models';
import { BookingService } from '@/services';
import { useBookingStore } from '@/store';

import CoupleTag from './tags/CoupleTag';

interface TreatmentListProps {
  isOpen: boolean;
  onClose: () => void;
  customerIndex: number | null;
  onTreatmentAdded?: (customerIndex: number, treatmentIndex: number) => void;
}

const requiresCouple = (t?: Treatment | null) =>
  !!t && t.options.some((o) => o.paxCount > 1);

const TreatmentList: React.FC<TreatmentListProps> = ({
  isOpen,
  onClose,
  customerIndex,
  onTreatmentAdded,
}) => {
  const { t } = useTranslation();
  const { customers, setAvailableTreatments } = useBookingStore();

  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTreatment, setSelectedTreatment] = useState<Treatment | null>(
    null,
  );
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
  const [isAssignToAll, setIsAssignToAll] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [selectedPartnerIndex, setSelectedPartnerIndex] = useState<
    number | null
  >(null);
  const [partnerSelectionError, setPartnerSelectionError] =
    useState<string>('');

  // Reset all selection/filter state whenever the modal closes so it reopens fresh
  useEffect(() => {
    if (!isOpen) {
      setSelectedCategory('All');
      setSearchQuery('');
      setSelectedTreatment(null);
      setSelectedOptionId(null);
      setIsAssignToAll(false);
      setShowFullDescription(false);
      setSelectedPartnerIndex(null);
      setPartnerSelectionError('');
    }
  }, [isOpen]);

  // Load treatments when opened / dependencies change
  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setLoading(true);
      try {
        const idx = customerIndex ?? 0;
        const data = await BookingService.getTreatments(customers, idx);
        setTreatments(data);
        setAvailableTreatments(data);
      } catch (e) {
        console.error('Failed to load treatments', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, customerIndex, customers, setAvailableTreatments]);

  // Reset partner selection when the selected option changes
  useEffect(() => {
    setSelectedPartnerIndex(null);
    setPartnerSelectionError('');
  }, [selectedOptionId]);

  // Check if the selected option requires partner selection (paxCount > 1)
  const selectedOption = selectedTreatment?.options.find(
    (option) => option.optionId === selectedOptionId,
  );
  const requiresPartnerSelection =
    selectedOption && selectedOption.paxCount > 1;

  // Generate partner dropdown options (exclude current customer)
  const partnerOptions = useMemo(() => {
    if (customerIndex === null) return [];

    return customers
      .map((customer, index) => {
        if (index === customerIndex) return null; // Exclude current customer

        const customerName =
          customer.name || t('treatment.customer', { index: index + 1 });

        return {
          value: index,
          label: customerName,
          icon: <BsPersonPlus className="h-4 w-4" />,
        };
      })
      .filter(
        (option): option is NonNullable<typeof option> => option !== null,
      );
  }, [customers, customerIndex, t]);

  const categories = useMemo(
    () => [
      { id: 'All', name: t('components.treatmentCategory.all') },
      ...Array.from(new Set(treatments.map((t) => t.categoryName))).map(
        (c) => ({
          id: c,
          name: c,
        }),
      ),
    ],
    [treatments, t],
  );

  const filteredTreatments = useMemo(
    () =>
      treatments.filter((treatment) => {
        const matchesCategory =
          selectedCategory === 'All' ||
          treatment.categoryName === selectedCategory;
        const matchesSearch = treatment.name
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
      }),
    [treatments, selectedCategory, searchQuery],
  );

  const handleTreatmentClick = (t: Treatment) => {
    setSelectedTreatment(t);
    setSelectedOptionId(null);
    setIsAssignToAll(false);
    setShowFullDescription(false);
    setSelectedPartnerIndex(null);
    setPartnerSelectionError('');
  };

  const handleConfirm = async () => {
    if (!selectedTreatment || !selectedOptionId) return;

    // Validate partner selection if required
    if (requiresPartnerSelection && selectedPartnerIndex === null) {
      setPartnerSelectionError(t('treatment.pleaseSelectPartner'));
      return;
    }

    setPartnerSelectionError('');
    setConfirming(true);

    try {
      await BookingService.addTreatment(
        customerIndex ?? 0,
        selectedTreatment.treatmentId,
        selectedOptionId,
        isAssignToAll,
        selectedPartnerIndex ?? undefined,
      );

      // Notify parent that a treatment was added so it can highlight/scroll
      const idx = customerIndex ?? 0;
      const updatedCustomer = useBookingStore.getState().customers[idx];
      const newTreatmentIndex = updatedCustomer?.treatmentItems?.length
        ? updatedCustomer.treatmentItems.length - 1
        : 0;
      onTreatmentAdded?.(idx, newTreatmentIndex);
    } catch (e) {
      console.error('Failed to choose treatment:', e);
    } finally {
      setConfirming(false);
      onClose();
    }
  };

  const renderTreatmentDetail = () => {
    if (!selectedTreatment) {
      return (
        <div className="text-secondary-text flex flex-1 items-center justify-center px-4 text-sm">
          {t('treatment.selectTreatment')}
        </div>
      );
    }
    const maxLength = 120;
    const needsTruncation = selectedTreatment.description.length > maxLength;
    const truncated =
      selectedTreatment.description.substring(0, maxLength) + '...';
    const displayText =
      showFullDescription || !needsTruncation
        ? selectedTreatment.description
        : truncated;
    return (
      <div className="flex h-full flex-col justify-between px-4 pb-4 md:px-6 md:py-6">
        <div className="h-11/12 overflow-y-auto">
          <div className="mb-4 h-[152px] overflow-hidden rounded-lg border border-gray-200 bg-white md:mb-6 md:h-[180px]">
            <img
              src={selectedTreatment.imageUrl}
              alt={selectedTreatment.name}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="mb-4 md:mb-6">
            <div className="mb-1 text-xl leading-7 font-semibold text-black md:mb-2 md:text-base md:leading-[30px]">
              {selectedTreatment.name}
            </div>
            <div className="text-secondary-text leading-6 md:text-[15px] md:leading-[22px]">
              <p>
                <span>{displayText}</span>
                {needsTruncation && (
                  <button
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="text-primary ml-1 hover:underline"
                  >
                    {showFullDescription
                      ? t('treatment.showLess')
                      : t('treatment.seeMore')}
                  </button>
                )}
              </p>
            </div>
            {requiresCouple(selectedTreatment) && (
              <div className="mb-2 md:mb-3">
                <CoupleTag text={t('treatment.couple')} />
              </div>
            )}
            {selectedTreatment.remark &&
              selectedTreatment.remark.trim() !== '' && (
                <div className="mt-2 flex items-center gap-1.5 text-sm text-amber-600 md:mt-3">
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 9v4" />
                    <path d="M12 17h.01" />
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                  </svg>
                  {selectedTreatment.remark}
                </div>
              )}
          </div>
          <div className="mb-4 md:mb-6">
            <div className="text-secondary-text mb-2 leading-7 font-semibold md:mb-3 md:leading-6">
              {t('treatment.selectDuration')}
            </div>
            <div className="flex flex-col gap-2 md:gap-3">
              {selectedTreatment.options.map((option) => {
                const isSelected =
                  selectedOptionId === option.optionId && option.available;
                const showStrike =
                  option.originalPrice > 0 &&
                  option.retailPrice > 0 &&
                  option.retailPrice < option.originalPrice;
                const priceToShow =
                  option.retailPrice > 0
                    ? option.retailPrice
                    : option.originalPrice;
                return (
                  <button
                    key={option.optionId}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() =>
                      option.available && setSelectedOptionId(option.optionId)
                    }
                    disabled={!option.available}
                    className={`flex w-full flex-col gap-1 rounded-lg border bg-white p-2 md:p-3 ${isSelected ? 'border-primary' : 'border-transparent'} ${option.available && !isSelected ? 'hover:border-primary' : ''} ${option.available ? 'cursor-pointer' : 'cursor-not-allowed'} transition-colors`}
                  >
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="size-5">
                        {isSelected ? (
                          <RiCheckboxCircleFill className="text-primary mb-10 size-5" />
                        ) : (
                          <div
                            aria-hidden="true"
                            className={`mt-0.5 size-4 rounded-full border ${option.available ? 'border-border-default' : 'border-text-disabled'}`}
                          />
                        )}
                      </div>
                      <div
                        className={`${option.available ? 'text-primary-text' : 'text-text-disabled'} flex grow basis-0 flex-col justify-center text-left leading-[0] not-italic md:text-[15px]`}
                      >
                        <p className="leading-[24px] md:leading-[22px]">
                          {option.duration} {t('common.min')}
                        </p>
                      </div>
                      {showStrike && (
                        <div className="text-text-disabled flex flex-col justify-center text-left leading-[0] not-italic md:text-sm">
                          <p className="leading-[24px] line-through md:leading-[20px]">
                            THB {option.originalPrice.toLocaleString()}
                          </p>
                        </div>
                      )}
                      <div
                        className={`flex w-[70px] flex-col justify-center text-right leading-[0] not-italic md:w-[80px] md:text-[15px] ${option.available ? 'text-primary-text' : 'text-text-disabled'}`}
                      >
                        <p className="leading-[24px] md:leading-[22px]">
                          THB {priceToShow.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {!option.available && option.suggestion && (
                      <div className="text-error pl-6 text-left text-sm leading-5 md:pl-8 md:text-[13px] md:leading-[18px]">
                        {option.suggestion}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Partner Selection Dropdown (only shown if paxCount > 1) */}
          {requiresPartnerSelection && (
            <div className="mb-4 px-0.5 md:mb-6">
              <Dropdown
                label={t('treatment.selectPartner')}
                placeholder={t('treatment.selectPartner')}
                options={partnerOptions}
                value={selectedPartnerIndex}
                onChange={(value) => {
                  setSelectedPartnerIndex(value as number);
                  setPartnerSelectionError('');
                }}
                error={partnerSelectionError}
                prefixIcon={<AiOutlineTeam className="size-5" />}
              />
            </div>
          )}

          {/* Assign to All Customers checkbox (hidden for couple treatments) */}
          {!requiresCouple(selectedTreatment) && (
            <div className="mb-4 md:mb-6">
              <button
                onClick={() => setIsAssignToAll(!isAssignToAll)}
                className="flex items-center gap-2 md:gap-3"
                type="button"
              >
                <div className="relative size-5">
                  <div
                    className={`absolute inset-0 rounded ${isAssignToAll ? 'bg-primary' : ''}`}
                  >
                    {!isAssignToAll && (
                      <div className="border-border-default absolute inset-0 rounded border" />
                    )}
                    {isAssignToAll && (
                      <div className="absolute inset-[15%]">
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 14 10"
                          fill="none"
                        >
                          <path
                            d="M1 5L5 9L13 1"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
                <span className="text-text-special leading-6 md:text-[15px] md:leading-[22px]">
                  {t('treatment.assignToAllCustomers')}
                </span>
              </button>
            </div>
          )}
        </div>
        <div className="h-1/12 bg-white pt-2 pb-4 md:block">
          <Button
            onClick={handleConfirm}
            disabled={!selectedOptionId}
            variant={selectedOptionId ? 'primary' : 'secondary'}
          >
            {t('treatment.select')}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <ResponsiveModalPage
      isOpen={isOpen}
      onClose={onClose}
      title={t('treatment.selectTreatment')}
      desktopWidthClass="md:w-2/3"
      desktopHeightClass="md:h-[85vh]"
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
        {/* Left: list */}
        <div className="flex min-h-0 flex-col overflow-hidden border-r border-gray-200 md:h-full md:w-1/2">
          <div className="px-4 pt-4 md:pt-4">
            <div className="mb-4">
              <TextInput
                placeholder={t('treatment.findTreatment')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                suffixIcon={<TbZoom className="h-5 w-5 text-gray-400" />}
                className="w-full"
              />
            </div>
            <div className="mb-4 border-b border-gray-200">
              <div className="flex h-12 items-center overflow-x-auto">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`relative flex-shrink-0 px-3 py-2 text-center whitespace-nowrap ${selectedCategory === category.id ? 'border-primary border-b-[3px] text-black' : 'text-primary-text'}`}
                  >
                    <span className="leading-7">{category.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 [-webkit-overflow-scrolling:touch]">
            {loading ? (
              <div className="space-y-0 pb-2">
                {Array.from({ length: 8 }).map((_, index) => (
                  <Fragment key={`skeleton-${index}`}>
                    <div className="flex items-center gap-2 py-3">
                      <div className="h-[60px] w-[70px] flex-shrink-0 animate-pulse rounded-lg bg-gray-200"></div>
                      <div className="flex h-[60px] flex-1 flex-col justify-between">
                        <div className="flex-1">
                          <div className="mb-1 h-4 w-3/4 animate-pulse rounded bg-gray-200"></div>
                          {/* <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200"></div> */}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="h-4 w-4 animate-pulse rounded bg-gray-200"></div>
                          <div className="h-3.5 w-24 animate-pulse rounded bg-gray-200"></div>
                        </div>
                      </div>
                    </div>
                  </Fragment>
                ))}
              </div>
            ) : (
              <div className="space-y-0 pb-2">
                {filteredTreatments.map((treatment, index) => {
                  const hasCoupleTreatment = requiresCouple(treatment);
                  const isUnavailable = !treatment.available;
                  return (
                    <React.Fragment key={treatment.treatmentId}>
                      <div
                        onClick={() => handleTreatmentClick(treatment)}
                        className={`flex cursor-pointer items-center gap-2 py-3 transition-colors ${selectedTreatment?.treatmentId === treatment.treatmentId ? 'bg-gray-100' : 'hover:bg-primary-50'}`}
                      >
                        <div className="h-[60px] w-[70px] flex-shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-white">
                          <img
                            src={treatment.imageUrl}
                            alt={treatment.name}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="flex h-[60px] flex-1 flex-col justify-between">
                          <div className="flex-1">
                            <h3
                              className={`line-clamp-2 leading-5 font-semibold ${isUnavailable ? 'text-text-disabled' : 'text-primary-text'}`}
                            >
                              {treatment.name}
                            </h3>
                            {hasCoupleTreatment && (
                              <div className="mt-1.5">
                                <CoupleTag text={t('treatment.couple')} />
                              </div>
                            )}
                          </div>
                          <div
                            className={`flex items-center justify-between gap-2 ${hasCoupleTreatment ? 'pt-1' : ''}`}
                          >
                            <div className="flex items-center gap-2">
                              <div className="flex h-4 w-4 items-center justify-center">
                                <BsTags className="text-text-muted size-4" />
                              </div>
                              <span className="text-secondary-text text-sm leading-[18px]">
                                From THB {treatment.startingPrice}
                              </span>
                            </div>
                            {isUnavailable && (
                              <div className="text-error text-sm">
                                {t('treatment.unavailable')}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {index < filteredTreatments.length - 1 && (
                        <div className="bg-divider h-px" />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
            {!loading && filteredTreatments.length === 0 && (
              <div className="py-8 text-center text-gray-500">
                {treatments.length === 0
                  ? t('treatment.noTreatments')
                  : t('treatment.noTreatmentsAfterSearch')}
              </div>
            )}
          </div>
        </div>
        {/* Right: detail (desktop only) */}
        <div className="hidden h-full min-h-0 flex-col overflow-hidden md:flex md:w-1/2">
          {renderTreatmentDetail()}
        </div>
      </div>
      {/* Mobile BottomSheet (detail) */}
      <div className="md:hidden">
        <ResponsiveModalBottomSheet
          isOpen={!!selectedTreatment}
          onClose={() => setSelectedTreatment(null)}
          heightClass="h-[85vh]"
        >
          {renderTreatmentDetail()}
        </ResponsiveModalBottomSheet>
      </div>
      <LoadingOverlay isVisible={confirming} />
    </ResponsiveModalPage>
  );
};

export default TreatmentList;
